import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiInfo, FiCheckCircle, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import AdminLayout from '../layouts/AdminLayout';
import {
  getAllNotifications,
  createNotification,
  updateNotification,
  deleteNotification,
  toggleNotificationStatus
} from '../utils/notifications';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    link_url: '',
    link_text: '',
    is_active: true
  });

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    const result = await getAllNotifications();
    if (result.success) {
      setNotifications(result.data || []);
    }
    setLoading(false);
  };

  const handleOpenModal = (notification = null) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title,
        message: notification.message,
        type: notification.type,
        link_url: notification.link_url || '',
        link_text: notification.link_text || '',
        is_active: notification.is_active
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        message: '',
        type: 'info',
        link_url: '',
        link_text: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNotification(null);
    setMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    let result;
    if (editingNotification) {
      result = await updateNotification(editingNotification.id, formData);
    } else {
      result = await createNotification(formData);
    }

    setMessage(result.message);
    setSubmitting(false);

    if (result.success) {
      await loadNotifications();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const result = await toggleNotificationStatus(id, !currentStatus);
    if (result.success) {
      await loadNotifications();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الإشعار؟')) {
      const result = await deleteNotification(id);
      if (result.success) {
        await loadNotifications();
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <FiAlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <FiAlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FiInfo className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'success':
        return 'نجاح';
      case 'warning':
        return 'تحذير';
      case 'error':
        return 'خطأ';
      default:
        return 'معلومات';
    }
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-yellow mb-2">إدارة الإشعارات</h1>
            <p className="text-white/70">
              إنشاء وإدارة إشعارات البانر للمستخدمين
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-yellow text-[#05339c] px-6 py-3 rounded-xl hover:bg-yellow/90 transition-all duration-300 font-bold shadow-lg"
          >
            <FiPlus className="w-5 h-5" />
            <span>إشعار جديد</span>
          </button>
        </div>
      </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="text-white/60 text-sm mb-2">إجمالي الإشعارات</div>
            <div className="text-4xl font-bold text-yellow">{notifications.length}</div>
          </div>
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="text-white/60 text-sm mb-2">الإشعارات النشطة</div>
            <div className="text-4xl font-bold text-green-400">
              {notifications.filter(n => n.is_active).length}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow mx-auto mb-4"></div>
            <div className="text-white/70">جاري التحميل...</div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-8 text-center">
            <div className="text-white/70 mb-4">لا توجد إشعارات حتى الآن</div>
            <button
              onClick={() => handleOpenModal()}
              className="text-yellow hover:text-yellow/80 font-bold"
            >
              إنشاء أول إشعار
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`bg-dark-blue border-2 border-yellow rounded-2xl p-6 ${
                  !notification.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="mt-1">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-bold text-yellow">
                          {notification.title}
                        </h3>
                        <span className="px-3 py-1 text-xs font-medium bg-blue/20 text-white/80 rounded-full">
                          {getTypeLabel(notification.type)}
                        </span>
                        {notification.is_active ? (
                          <span className="px-3 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                            نشط
                          </span>
                        ) : (
                          <span className="px-3 py-1 text-xs font-medium bg-white/10 text-white/40 rounded-full">
                            معطل
                          </span>
                        )}
                      </div>
                      <p className="text-white/80 mb-2">{notification.message}</p>
                      {notification.link_url && (
                        <a
                          href={notification.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-yellow hover:text-yellow/80 underline inline-block"
                        >
                          {notification.link_text || notification.link_url}
                        </a>
                      )}
                      <div className="text-xs text-white/40 mt-3">
                        تم الإنشاء: {new Date(notification.created_at).toLocaleString('ar-EG')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleStatus(notification.id, notification.is_active)}
                      className="p-2 text-white/70 hover:bg-white/10 rounded-lg transition-colors"
                      title={notification.is_active ? 'تعطيل' : 'تفعيل'}
                    >
                      {notification.is_active ? (
                        <FiEyeOff className="w-5 h-5" />
                      ) : (
                        <FiEye className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleOpenModal(notification)}
                      className="p-2 text-yellow hover:bg-yellow/10 rounded-lg transition-colors"
                      title="تعديل"
                    >
                      <FiEdit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(notification.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="حذف"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 rounded-t-2xl">
              <h2 className="text-xl font-bold text-[#05339c]">
                {editingNotification ? 'تعديل الإشعار' : 'إشعار جديد'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {message && (
                <div className={`p-4 rounded-xl border-2 ${
                  message.includes('نجاح') 
                    ? 'bg-green-500/20 border-green-500 text-green-400' 
                    : 'bg-red-500/20 border-red-500 text-red-400'
                }`}>
                  {message}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-yellow mb-2">
                  العنوان *
                </label>
                <input
dir="ltr"                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-blue/20 border-2 border-yellow rounded-xl focus:ring-2 focus:ring-yellow focus:border-yellow text-white placeholder-white/50"
                  placeholder="عنوان الإشعار"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-yellow mb-2">
                  الرسالة *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full px-4 py-3 bg-blue/20 border-2 border-yellow rounded-xl focus:ring-2 focus:ring-yellow focus:border-yellow text-white placeholder-white/50"
                  placeholder="نص الإشعار"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-yellow mb-2">
                  النوع *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-blue/20 border-2 border-yellow rounded-xl focus:ring-2 focus:ring-yellow focus:border-yellow text-white"
                >
                  <option value="info">معلومات (أزرق)</option>
                  <option value="success">نجاح (أخضر)</option>
                  <option value="warning">تحذير (أصفر)</option>
                  <option value="error">خطأ (أحمر)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-yellow mb-2">
                  رابط (اختياري)
                </label>
                <input
dir="ltr"                  type="url"
                  name="link_url"
                  value={formData.link_url}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-blue/20 border-2 border-yellow rounded-xl focus:ring-2 focus:ring-yellow focus:border-yellow text-white placeholder-white/50"
                  placeholder="https://example.com"
                />
              </div>

              {formData.link_url && (
                <div>
                  <label className="block text-sm font-medium text-yellow mb-2">
                    نص الرابط (اختياري)
                  </label>
                  <input
dir="ltr"                    type="text"
                    name="link_text"
                    value={formData.link_text}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-blue/20 border-2 border-yellow rounded-xl focus:ring-2 focus:ring-yellow focus:border-yellow text-white placeholder-white/50"
                    placeholder="اضغط هنا"
                  />
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-blue/20 border-2 border-yellow rounded-xl">
                <input
dir="ltr"                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-5 h-5 appearance-none rounded border-2 border-yellow bg-transparent cursor-pointer checked:bg-yellow checked:border-yellow relative flex-shrink-0 after:content-['✓'] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:text-[#05339c] after:font-bold after:text-sm checked:after:block after:hidden transition-all duration-200"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-white cursor-pointer">
                  تفعيل الإشعار مباشرة
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-3 border-2 border-white/20 text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-medium"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-yellow text-[#05339c] rounded-xl hover:bg-yellow/90 transition-all duration-300 font-bold disabled:opacity-50"
                >
                  {submitting ? 'جاري الحفظ...' : (editingNotification ? 'تحديث' : 'إنشاء')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Notifications;

